import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookMedical,
  faArrowLeft,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../Context/AuthContext";
import styles from "./Auth.module.css";
import logoBg from "../static/logobgr.png";
import clinicLogo from "../static/logo.png";

const Auth = ({ initialMode = "login" }) => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // State Management
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [role, setRole] = useState("patient");
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    password: "",
    confirmPassword: "",
  });

  // Automatically toggle form state if route prop changes
  useEffect(() => {
    setIsLogin(initialMode === "login");
  }, [initialMode]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!isLogin) {
      setRole("patient");
    }
  }, [isLogin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        // ─── SIGN IN FLOW ───────────────────────────────────────────
        const loginPayload = {
          email: formData.email,
          password: formData.password,
          role: role,
        };

        const response = await axios.post(`/api/${role}/login`, loginPayload);

        if (response.data.status) {
          toast.success("Welcome back!");
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("userId", response.data.userId);
          if (response.data.userName) {
            localStorage.setItem("userName", response.data.userName);
          }

          login();
          navigate(role === "doctor" ? "/doctor-dashboard" : "/patient-dashboard");
        } else {
          toast.error(response.data.message || "Invalid email or password.");
        }
      } else {
        // ─── CREATE ACCOUNT FLOW ────────────────────────────────────
        if (formData.password !== formData.confirmPassword) {
          toast.error("Password and Confirm Password do not match.");
          setIsLoading(false);
          return;
        }

        const nameParts = formData.name.trim().split(" ");
        const firstName = nameParts[0] || "User";
        const lastName = nameParts.slice(1).join(" ") || ".";

        const regPayload = {
          name: formData.name,
          firstName: firstName,
          lastName: lastName,
          email: formData.email,
          password: formData.password,
          contactNumber: formData.contactNumber,
          mobileNumber: formData.contactNumber,
          role: role,
        };

        const endpoint =
          role === "doctor" ? "/api/doctor/register" : "/api/patient/register";
        const response = await axios.post(endpoint, regPayload);

        if (response.data.status) {
          toast.success("Account created successfully! Please sign in.");
          setIsLogin(true); // Smoothly toggle to login form
          setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
        } else {
          toast.error(
            response.data.message || "Registration failed. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Authentication Error:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred communicating with the server. Please check your network."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      {/* ─── 1. LEFT COLUMN (The Visual Anchor - Desktop Only) ───────── */}
      <div className={styles.leftCol}>
        <div className={styles.leftOverlay} />

        <div className={styles.leftContentTop}>
          <Link to="/" className={styles.brandLogo}>
            <img src={logoBg} alt="ReformMe Logo" className={styles.leftLogoImg} />
            <span>ReformMe Healthcare</span>
          </Link>
        </div>

        <div className={styles.leftContentBottom}>
          <div className={styles.quoteWrap}>
            <p className={styles.quoteText}>
              “Restoring movement, renewing lives. Precision physiotherapy
              designed around your complete recovery.”
            </p>
            <p className={styles.quoteAuthor}>
              Verified Clinical Excellence • Gurugram
            </p>
          </div>
        </div>
      </div>

      {/* ─── 2. RIGHT COLUMN (The Auth Form Container) ───────────────── */}
      <div className={styles.rightCol}>
        <div className={styles.formCard}>
          {/* Back Navigation Button */}
          <Link to="/" className={styles.backNavBtn}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Home</span>
          </Link>

          {/* Form Header */}
          <div className={styles.formHeader}>
            <Link to="/" className={styles.formBrandLogo}>
              <img
                src={clinicLogo}
                alt="ReformMe Healthcare"
                className={styles.clinicLogoImg}
              />
              <div className={styles.brandTextStack}>
                <span className={styles.brandTextTop}>ReformMe</span>
                <span className={styles.brandTextBottom}>Healthcare</span>
              </div>
            </Link>
            <h1 className={styles.headline}>
              {isLogin ? "Welcome Back" : "Join ReformMe"}
            </h1>
            <p className={styles.subheadline}>
              {isLogin
                ? "Please enter your details to access your patient portal."
                : "Create your unified portal account to manage appointments & care."}
            </p>
          </div>

          {/* Account Type / Role Tabs (Doctor Portal commented out on Registration Page) */}
          {isLogin && (
            <div className={styles.roleTabs}>
              <button
                type="button"
                onClick={() => setRole("patient")}
                className={`${styles.roleTab} ${
                  role === "patient" ? styles.roleTabActive : ""
                }`}
              >
                Patient Account
              </button>
              <button
                type="button"
                onClick={() => setRole("doctor")}
                className={`${styles.roleTab} ${
                  role === "doctor" ? styles.roleTabActive : ""
                }`}
              >
                Doctor Portal
              </button>
            </div>
          )}

          {/* Unified Form */}
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Full Name & Phone (Registration Only) */}
            {!isLogin && (
              <>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="e.g. Rahul Verma"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="contactNumber" className={styles.label}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    required
                    placeholder="+91 90249 89935"
                    value={formData.contactNumber}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
              </>
            )}

            {/* Standard Email */}
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                placeholder="patient@example.com"
                value={formData.email}
                onChange={handleInputChange}
                className={styles.input}
              />
            </div>

            {/* Password */}
            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                placeholder="••••••••••••"
                value={formData.password}
                onChange={handleInputChange}
                className={styles.input}
              />
            </div>

            {/* Confirm Password (Registration Only) */}
            {!isLogin && (
              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword" className={styles.label}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  required
                  placeholder="••••••••••••"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={styles.input}
                />
              </div>
            )}

            {/* Forgot Password Link (Login Only) */}
            {isLogin && (
              <div className={styles.actionRow}>
                <Link to="/forgot-password" className={styles.forgotLink}>
                  Forgot Password?
                </Link>
              </div>
            )}

            {/* Primary Submit CTA */}
            <button type="submit" disabled={isLoading} className={styles.submitBtn}>
              <span>
                {isLoading
                  ? "Processing..."
                  : isLogin
                  ? "Sign In to Portal"
                  : "Create Account"}
              </span>
              {!isLoading && <FontAwesomeIcon icon={faArrowRight} />}
            </button>
          </form>

          {/* Smooth Mode Toggle */}
          <div className={styles.toggleText}>
            <span>
              {isLogin
                ? "Don't have an account?"
                : "Already have an account?"}
            </span>
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className={styles.toggleBtn}
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
